import React, {useEffect, useState} from "react";
import MobilePageLayout from "../../../layouts/MobilePageLayout";
import {ScreenProps} from "../../../../../utils/screen-props-helper";
import {AssetStackParamList} from "../AssetStack";
import {useLazyGetAssetQuery} from "../../../../../store/services/api/asset-api";
import {useAddTicketMutation} from "../../../../../store/services/api/ticket-api";
import {FlatList} from "react-native";
import MobileForm from "../../../layouts/MobileForm";
import * as ImagePicker from "expo-image-picker";
import {compressImage} from "../../../../../utils/imageUtils";
import {Button, Flex, Form, Image, Select,Input, notification, Skeleton} from "antd";
import {FileImageOutlined, CameraOutlined} from "@ant-design/icons";
import { useGetAllSitesQuery, useGetAllTeamsQuery } from "../../../../../store/services/api/site-api";
import SiteTeamSelector from "../../../../../components/form-controls/SiteTeamSelector";

export default function AssetCreateTicketScreen({route, navigation}: ScreenProps<AssetStackParamList, 'createTicket'>) {
  const assetId = route.params?.assetId;
  const [getAsset, { data: asset, isFetching: assetFetching }] = useLazyGetAssetQuery();
  const [createTicket, {isLoading: createLoading}] = useAddTicketMutation();
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
    const { data: sites } = useGetAllSitesQuery(null);
    const { data: allTeams } = useGetAllTeamsQuery(null);
    const [filteredTeams, setFilteredTeams] = useState<any[]>([]);
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  useEffect(() => {
    if (assetId) {
      getAsset(assetId)
        .unwrap()
        .then((asset) => {
          form.setFieldValue('description', asset.description);
          form.setFieldValue('assetId', assetId);
        });
    }
  }, [assetId]);

  const [form] = Form.useForm();

  const onSubmit = async (data: any ) => {
    data.images = await Promise.all(
      selectedImages.map(uri => compressImage(uri))
    );
    
    const finalData = {
      ...data,
      entrySource:
        data.entrySource = "QR Code"
    };

    createTicket(finalData)
      .unwrap()
      .then(() => {
        notification.info({
          message: `Success`,
          description: 'Successfully created ticket',
          placement: 'bottomRight',
          duration: 3,
        });
        navigation.navigate("asset", { uniqueRef: asset.uniqueReference });
      });
  }

  const takeImage = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== "granted") {
      alert("Permission to access the camera is required!");
      return;
    }

    const result = await ImagePicker.launchCameraAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      base64: true,
    });

    if (!result.canceled) {
      setSelectedImages([...selectedImages, result.assets[0].uri]);
    }
  };

  const pickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      alert("Permission to access the photos is required!");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 1,
      base64: true,
      allowsMultipleSelection: true
    });

    if (!result.canceled) {
      setSelectedImages([...selectedImages, result.assets[0].uri]);
    }
  };

  const deleteImage = (index) => {
    setSelectedImages(selectedImages.filter((_, idx) => idx !== index));
  };


   const handleOnSiteChange = (siteId: string | null) => {
  setSelectedSiteId(siteId);
  
  if (!siteId) {
    form.setFieldsValue({ teamId: undefined });
    setFilteredTeams([]);
    return;
  }

  if (allTeams) {
    const filtered = allTeams.filter((t: any) => t.siteId === siteId);
    setFilteredTeams(filtered);
    const currentTeamId = form.getFieldValue("teamId");
    if (!currentTeamId) {
      const defaultTeamName = filtered.find((t: any) =>
        t.name.includes("Infrastructure")
      )?.name; 
      const defaultTeam = filtered.find((t: any) => t.name === defaultTeamName);
      if (defaultTeam) {
        form.setFieldsValue({ teamId: defaultTeam.id });
      }
    }
  }
};


  useEffect(() => {
    if (sites && allTeams && !form.getFieldValue("siteId")) {
      const pgcSite = sites.find((s: any) => s.name === "GC");
      if (pgcSite) {
        const pgcTeam = allTeams.find(
          (t: any) =>
            t.siteId === pgcSite.id && t.name === "Infrastructure"
        );
        form.setFieldsValue({
          siteId: pgcSite.id,
          teamId: pgcTeam?.id,
        });
        setSelectedSiteId(pgcSite.id);
        setFilteredTeams(
          allTeams.filter((t: any) => t.siteId === pgcSite.id)
        );
      }
      console.log(form.getFieldValue("siteId"));
      console.log(form.getFieldValue("teamId"));
    }
  }, [sites, allTeams]);

  return (
    <MobilePageLayout>
      <MobileForm>
        <Skeleton
          active
          loading={assetFetching}
        >
            <Form
              form={form}
              onFinish={onSubmit}
              disabled={createLoading || assetFetching}
            >
              <Form.Item
                name='assetId'
                initialValue={assetId}
                hidden
              />
              <Form.Item
                name='description'
                label='Item Description'
              >
                <Input disabled />
              </Form.Item>
              <Form.Item
                name='issue'
                label='Issue'
                rules={[{required: true, message: 'Please enter an issue'}]}
              >
                <Input.TextArea
                  showCount
                  rows={8}
                  maxLength={200}
                />
              </Form.Item>
              <Form.Item
                name='contactEmail'
                label='Contact Details'
                rules={[{required: true, message: 'Please enter your contact details'}]}
              >
                <Input.TextArea rows={2} />
              </Form.Item>

              <Form.Item name="siteId" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Form.Item name="teamId" hidden>
                <Input type="hidden" />
              </Form.Item>

              <FlatList
                horizontal
                data={selectedImages ?? []}
                renderItem={({item, index}) => (
                  <Image
                    preview={false}
                    onClick={() => deleteImage(index)}
                    style={{paddingInline: 2, objectFit: 'cover'}}
                    key={`ticket-images-${index}`}
                    src={item}
                    width={96}
                    height={96}
                  />
                )}
              />

              <Flex gap={4} style={{paddingBlock: '24px'}}>
                <Button
                  onClick={pickImage}
                  disabled={selectedImages.length >= 5}
                  size='large'
                  icon={<FileImageOutlined />}
                />
                <Button
                  onClick={takeImage}
                  disabled={selectedImages.length >= 5}
                  size='large'
                  icon={<CameraOutlined />}
                />
                <Form.Item
                  required
                  shouldUpdate={(prev, curr) =>
                    prev.siteId !== curr.siteId || prev.teamId !== curr.teamId
                  }
                  
                >
                  {() => (
                    <>
                      <SiteTeamSelector
                        sites={sites}
                        teams={allTeams}
                        value={{
                          siteId: form.getFieldValue("siteId"),
                          teamId: form.getFieldValue("teamId"),
                        }}
                        onChange={(siteId, teamId) => {
                          form.setFieldsValue({ siteId, teamId });
                          handleOnSiteChange(siteId);
                        }}
                      />
                    </>
                  )}
                </Form.Item>

                <div style={{marginLeft: 'auto'}}>
                  <Button loading={createLoading} type='primary' htmlType='submit'>Submit</Button>
                </div>
              </Flex>
            </Form>
        </Skeleton>
      </MobileForm>
    </MobilePageLayout>
  )
}
